import React, { useEffect, useState, useCallback, useRef } from "react";
import { Bell, CheckCheck, Ghost, Trophy, Dice5, Mic } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PushToggle from "@/components/PushToggle";
import { fireConfetti } from "@/components/Confetti";

const WIN_TYPES = new Set(["gufata_success", "challenge_won"]);
const SEEN_KEY = "fife_confetti_seen_v1";
const loadSeen = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); }
  catch { return new Set(); }
};
const saveSeen = (set) => {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(set).slice(-100))); } catch {}
};

const ICON = {
  gufata_received: Ghost,
  bet_settled: Dice5,
  standings_changed: Trophy,
  interview_published: Mic,
};
const COLOR = {
  gufata_received: "var(--gufata)",
  bet_settled: "var(--scommetti)",
  standings_changed: "var(--brand)",
  interview_published: "var(--orange)",
};

function timeAgo(iso) {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}g`;
  } catch { return ""; }
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const seenRef = useRef(loadSeen());

  const load = useCallback(async () => {
    if (!user?.id) return setData({ items: [], unread: 0 });
    try {
      const { data } = await api.get("/me/notifications?limit=30");
      setData(data);
      // Detect brand-new win notifications and fire confetti + celebratory toast.
      const seen = seenRef.current;
      const fresh = (data.items || []).filter(
        (n) => WIN_TYPES.has(n.type) && !n.read && !seen.has(n.id)
      );
      if (fresh.length > 0) {
        fresh.forEach((n) => seen.add(n.id));
        saveSeen(seen);
        // Delay slightly so the toast + confetti feel coordinated.
        setTimeout(() => {
          fireConfetti({ origin: fresh[0].type, count: 200 });
          const label = fresh[0].type === "challenge_won"
            ? "Tutto o Niente vinto! 🏆"
            : "Gufata riuscita! 🦉";
          toast.success(label, { description: fresh[0].body });
        }, 120);
      } else {
        // Still remember read/existing win notifications so we don't refire on remount.
        (data.items || []).forEach((n) => { if (WIN_TYPES.has(n.type)) seen.add(n.id); });
        saveSeen(seen);
      }
    } catch {}
  }, [user?.id]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAllRead = async ({ closeAfter = false } = {}) => {
    try {
      await api.post("/me/notifications/read-all");
      await load();
      if (closeAfter) setOpen(false);
    } catch {}
  };
  const markRead = async (id) => {
    try { await api.post(`/me/notifications/${id}/read`); load(); } catch {}
  };

  if (!user?.id) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notif-bell"
          className="relative p-2 rounded-full hover:bg-slate-100 text-slate-800 transition-colors"
          aria-label="Notifiche"
        >
          <Bell className={`w-5 h-5 ${data.unread > 0 ? "hover-wiggle" : ""}`} />
          {data.unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-black flex items-center justify-center px-1 ring-pulse bounce-in"
              style={{ background: "var(--orange)", color: "#ffffff" }}
              data-testid="notif-badge"
            >
              {data.unread > 99 ? "99+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[calc(100vw-1rem)] sm:w-[380px] bg-white border-slate-200 text-slate-900 p-0 max-h-[75vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-200">
          <div className="display font-bold">Notifiche{data.unread > 0 && <span className="ml-1.5 text-xs font-bold text-orange-600">· {data.unread}</span>}</div>
          <button
            onClick={() => markAllRead({ closeAfter: true })}
            disabled={data.unread === 0}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition shrink-0"
            data-testid="notif-read-all"
          >
            <CheckCheck className="w-3.5 h-3.5" />
            <span>Segna come lette</span>
          </button>
        </div>
        <PushToggle />
        <div className="flex-1 overflow-y-auto">
          {data.items.length === 0 ? (
            <div className="px-4 py-8 text-slate-500 text-sm text-center" data-testid="notif-empty">
              Nessuna notifica. Quando verrai gufato, quando cambia la classifica o quando una scommessa viene liquidata, comparirà qui.
            </div>
          ) : (
            <ul>
              {data.items.map((n) => {
                const Icon = ICON[n.type] || Bell;
                const col = COLOR[n.type] || "var(--brand)";
                return (
                  <li key={n.id}
                      onClick={() => !n.read && markRead(n.id)}
                      data-testid={`notif-item-${n.id}`}
                      className={`px-4 py-3 border-b border-slate-100 cursor-pointer hover:bg-slate-50 flex gap-3 ${n.read ? "opacity-60" : ""}`}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                         style={{ background: `${col}22`, color: col }}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-semibold truncate">{n.title}</div>
                        <div className="text-[10px] text-slate-400 shrink-0">{timeAgo(n.created_at)}</div>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{n.body}</div>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full mt-2 shrink-0" style={{ background: col }} />}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
